import { supabase } from './supabase';
import { FlowPolicyError, MRC_FLOW_LOCATION, MRC_FLOW_ORIGIN, parseFlowPolicyContext, parseFlowPolicyReceipt, type FlowEnableRequest } from './mrcFlowPolicy';
const endpoint = `https://paznetops-api-staging.fly.dev/portal/v1/locations/${MRC_FLOW_LOCATION}/collector-policy`;
export const mrcFlowUiEnabled = import.meta.env.VITE_MRC_FLOW_POLICY_ENABLED === 'true';

function denied() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('pazportal:network-authority-changed'));
  return new FlowPolicyError('access');
}

export async function readMrcFlowPolicy(locationId: string, userId: string, signal?: AbortSignal) {
  return parseFlowPolicyContext(await requestMrcFlowPolicy(locationId, userId, undefined, signal));
}
export async function enableMrcFlowPolicy(locationId: string, userId: string, request: FlowEnableRequest, signal?: AbortSignal) {
  const raw = await requestMrcFlowPolicy(locationId, userId, JSON.stringify(request), signal);
  try { return parseFlowPolicyReceipt(raw, request); } catch { throw new FlowPolicyError('unknown-outcome'); }
}

async function boundedJson(response: Response): Promise<unknown> {
  if (!response.headers.get('content-type')?.toLowerCase().startsWith('application/json') || !response.body) {
    throw new Error('response_contract');
  }
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  try {
    for (;;) {
      const part = await reader.read();
      if (part.done) break;
      size += part.value.byteLength;
      if (size > 65_536) throw new Error('response_bound');
      chunks.push(part.value);
    }
  } finally { await reader.cancel().catch(() => undefined); reader.releaseLock(); }
  const bytes = new Uint8Array(size);
  let offset = 0;
  for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
  return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
}

/** Internal transport for the closed context and enable-only request modules. Never accepts a URL. */
export async function requestMrcFlowPolicy(locationId: string, expectedUserId: string, body?: string, signal?: AbortSignal): Promise<unknown> {
  if (!mrcFlowUiEnabled || typeof window === 'undefined' || window.location.origin !== MRC_FLOW_ORIGIN) throw new FlowPolicyError('disabled');
  if (locationId !== MRC_FLOW_LOCATION) throw new FlowPolicyError('unconfigured');
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) controller.abort();
  const timeout = setTimeout(abort, 10_000);
  let dispatched = false;
  try {
    if (controller.signal.aborted) throw new FlowPolicyError('unavailable');
    const abortWait = () => { /* Assigned below so every listener can be removed. */ };
    let cancelWait = abortWait;
    const result = await Promise.race([
      supabase.auth.getSession(),
      new Promise<never>((_resolve, reject) => {
        cancelWait = () => reject(new FlowPolicyError('unavailable'));
        controller.signal.addEventListener('abort', cancelWait, { once: true });
      }),
    ]).finally(() => controller.signal.removeEventListener('abort', cancelWait));
    const { data: { session }, error } = result;
    if (controller.signal.aborted) throw new FlowPolicyError('unavailable');
    if (error || !session?.access_token || session.user.id !== expectedUserId || !expectedUserId) throw denied();
    dispatched = true;
    const response = await fetch(endpoint, {
      method: body === undefined ? 'GET' : 'PUT',
      headers: { Authorization: `Bearer ${session.access_token}`, ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
      body, signal: controller.signal, cache: 'no-store', credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer',
    });
    if (response.status === 401 || response.status === 403) throw denied();
    if (response.status === 409) throw new FlowPolicyError('conflict');
    if (response.status === 404) throw new FlowPolicyError('unconfigured');
    if (response.status === 400) throw new FlowPolicyError('invalid');
    if (!response.ok) throw new FlowPolicyError(body === undefined ? 'unavailable' : 'unknown-outcome');
    return await boundedJson(response);
  } catch (error) {
    if (error instanceof FlowPolicyError) throw error;
    throw new FlowPolicyError(body !== undefined && dispatched ? 'unknown-outcome' : 'unavailable');
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
}
